import React, { useMemo } from 'react';

import { Box, List, ListItem, Typography } from '@mui/material';
import { DataGridPro, GridColDef, GridRenderCellParams } from '@mui/x-data-grid-pro';

import { Bedbank } from '@luxuryescapes/contract-svc-bedbank';

import GridPagination from '~/components/Common/Elements/GridPagination';

import { CoreQuery } from './type';

interface Props {
  count: number;
  data: Array<Bedbank.ListSupplierProperty>;
  queries: CoreQuery;
  onPageChange: (page: number, limit: number) => void;
  onSortChange: (orderBy: CoreQuery['orderBy'], sortBy: CoreQuery['sortBy']) => void;
}

export default function PropertiesTable({ count, data, queries, onPageChange, onSortChange }: Props) {
  const columns: Array<GridColDef> = useMemo(
    () => [
      {
        field: 'supplierPropertyId',
        headerName: 'Id',
        disableColumnMenu: true,
        flex: 1,
        sortable: true,
        display: 'flex',
      },
      {
        field: 'supplier',
        headerName: 'Supplier',
        disableColumnMenu: true,
        flex: 1,
        sortable: false,
        display: 'flex',
      },
      {
        field: 'hotelName',
        headerName: 'Name',
        disableColumnMenu: true,
        flex: 2,
        sortable: true,
        display: 'flex',
      },
      {
        field: 'masterProperties',
        headerName: 'Master Properties',
        disableColumnMenu: true,
        flex: 4,
        sortable: false,
        display: 'flex',
        renderCell: ({ row }: GridRenderCellParams) => (
          <Box>
            <List>
              {row.masterProperties.map((masterProperty) => (
                <ListItem key={masterProperty.id}>
                  <Typography>
                    {masterProperty.id} ({masterProperty.supplier})
                  </Typography>
                </ListItem>
              ))}
            </List>
          </Box>
        ),
      },
      {
        field: 'countryCode',
        headerName: 'Address',
        disableColumnMenu: true,
        display: 'flex',
        sortable: false,
        renderCell: ({ row }: GridRenderCellParams) => (
          <div>
            <div>
              Country Code: <b>{row.countryCode}</b>
            </div>
            <div>{row.address}</div>
            <div>Latitude: {row.latitude}</div>
            <div>Longitude: {row.longitude}</div>
          </div>
        ),
        flex: 2,
      },
      {
        field: 'rating',
        headerName: 'Star Rating',
        disableColumnMenu: true,
        flex: 1,
        sortable: true,
        display: 'flex',
      },
      {
        field: 'numberOfRooms',
        headerName: 'Number Of Rooms',
        disableColumnMenu: true,
        flex: 1,
        sortable: false,
        display: 'flex',
      },
      {
        field: 'numberOfImages',
        headerName: 'Number Of Images',
        disableColumnMenu: true,
        flex: 1,
        sortable: false,
        display: 'flex',
      },
    ],
    [],
  );

  return (
    <>
      <DataGridPro
        columns={columns}
        disableRowSelectionOnClick
        getRowHeight={() => 'auto'}
        getRowId={(row) => row.supplierPropertyId}
        rows={data ?? []}
        pagination
        pageSizeOptions={[15, 25, 50, 100]}
        paginationModel={{ page: queries.page - 1, pageSize: queries.limit }}
        sortModel={[{ field: queries.sortBy, sort: queries.orderBy }]}
        onPaginationModelChange={({ page, pageSize }) => onPageChange(page + 1, pageSize)}
        onSortModelChange={(s) =>
          onSortChange(s[0]?.sort ?? 'asc', (s[0]?.field ?? queries.sortBy) as CoreQuery['sortBy'])
        }
        paginationMode="server"
        sortingMode="server"
        rowCount={count}
        density="compact"
        slots={{ pagination: GridPagination }}
        disableColumnFilter
        disableColumnSelector
        disableDensitySelector
      />
    </>
  );
}
